import type { TemplateMeta } from '../../api/templates';

export type TemplateGalleryProfile = {
  overviewKey: string;
  suitedKey: string;
  highlightKeys: [string, string, string];
  /** Fallback English when i18n key missing */
  overviewDefault: string;
  suitedDefault: string;
  highlightDefaults: [string, string, string];
};

type MatchRule = {
  ids?: string[];
  componentKeys?: string[];
  nameIncludes?: string[];
  profile: TemplateGalleryProfile;
};

const PROFILES: MatchRule[] = [
  {
    ids: ['t_static_1'],
    componentKeys: ['professional-classic'],
    nameIncludes: ['professional classic'],
    profile: {
      overviewKey: 'dcl_tpl_professional_classic_overview',
      suitedKey: 'dcl_tpl_professional_classic_suited',
      highlightKeys: ['dcl_tpl_professional_classic_h1', 'dcl_tpl_professional_classic_h2', 'dcl_tpl_professional_classic_h3'],
      overviewDefault:
        'A balanced, trustworthy layout with clear typography and structured sections. Professional Classic puts your name, role and contact details front and centre — ideal when you want a polished first impression without distraction.',
      suitedDefault: 'Executives, consultants, managers, lawyers, accountants and professionals who want a classic, credible digital identity.',
      highlightDefaults: [
        'Clean hierarchy that reads well at a glance',
        'Strong for LinkedIn, email signatures and networking',
        'Professional without feeling corporate or cold',
      ],
    },
  },
  {
    ids: ['t_static_2'],
    componentKeys: ['elegant-looking'],
    nameIncludes: ['elegant'],
    profile: {
      overviewKey: 'dcl_tpl_elegant_looking_overview',
      suitedKey: 'dcl_tpl_elegant_looking_suited',
      highlightKeys: ['dcl_tpl_elegant_looking_h1', 'dcl_tpl_elegant_looking_h2', 'dcl_tpl_elegant_looking_h3'],
      overviewDefault:
        'Refined accents and thoughtful contrast give this template a premium feel. Elegant Looking helps you present yourself with confidence — perfect when your personal brand should feel elevated and memorable.',
      suitedDefault: 'Brand strategists, luxury services, coaches, speakers and professionals who want sophistication with warmth.',
      highlightDefaults: [
        'Premium visual tone with subtle detail',
        'Great for client-facing and high-trust roles',
        'Balances personality with professionalism',
      ],
    },
  },
  {
    nameIncludes: ['orange modern'],
    profile: {
      overviewKey: 'dcl_tpl_orange_modern_overview',
      suitedKey: 'dcl_tpl_orange_modern_suited',
      highlightKeys: ['dcl_tpl_orange_modern_h1', 'dcl_tpl_orange_modern_h2', 'dcl_tpl_orange_modern_h3'],
      overviewDefault:
        'A clean, contemporary layout with generous spacing that keeps your key information front and centre. Orange Modern helps you introduce yourself clearly and makes it easy for visitors to connect with you.',
      suitedDefault:
        'Tech professionals, startups, designers, creators, product managers and anyone who prefers a structured, modern TCard.',
      highlightDefaults: [
        'Clean layout with generous spacing and easy readability',
        'Modern look that puts important details first',
        'Easy to use and view on mobile',
      ],
    },
  },
  {
    ids: ['t_static_3'],
    componentKeys: ['modern-clean'],
    nameIncludes: ['modern and clean', 'modern-clean'],
    profile: {
      overviewKey: 'dcl_tpl_modern_clean_overview',
      suitedKey: 'dcl_tpl_modern_clean_suited',
      highlightKeys: ['dcl_tpl_modern_clean_h1', 'dcl_tpl_modern_clean_h2', 'dcl_tpl_modern_clean_h3'],
      overviewDefault:
        'Minimal, spacious and focused on clarity. Modern and Clean strips away noise so visitors quickly understand who you are, what you do and how to reach you.',
      suitedDefault: 'Tech professionals, startups, designers, product managers and anyone who prefers a sharp, contemporary look.',
      highlightDefaults: [
        'Minimal layout with excellent readability',
        'Feels fresh, fast and product-led',
        'Ideal when content should do the talking',
      ],
    },
  },
  {
    ids: ['t_static_5', 't_1760657746541'],
    componentKeys: ['classic-modular'],
    nameIncludes: ['classic modular', 'modular'],
    profile: {
      overviewKey: 'dcl_tpl_classic_modular_overview',
      suitedKey: 'dcl_tpl_classic_modular_suited',
      highlightKeys: ['dcl_tpl_classic_modular_h1', 'dcl_tpl_classic_modular_h2', 'dcl_tpl_classic_modular_h3'],
      overviewDefault:
        'Organised into clear sections — about, contact, social and links — Classic Modular makes complex profiles easy to browse. Visitors find what they need without scrolling endlessly.',
      suitedDefault: 'Consultants, sales teams, multi-role professionals and businesses sharing several contact paths and links.',
      highlightDefaults: [
        'Modular sections for bio, contacts and links',
        'Easy to scan on mobile',
        'Flexible for growing digital identities',
      ],
    },
  },
  {
    ids: ['t_1760711434756', 't_1760653763366'],
    componentKeys: ['golden-view'],
    nameIncludes: ['golden'],
    profile: {
      overviewKey: 'dcl_tpl_golden_view_overview',
      suitedKey: 'dcl_tpl_golden_view_suited',
      highlightKeys: ['dcl_tpl_golden_view_h1', 'dcl_tpl_golden_view_h2', 'dcl_tpl_golden_view_h3'],
      overviewDefault:
        'Rich golden tones and premium styling signal quality and ambition. Golden View is for people who want their TCard to feel distinctive, confident and high-end.',
      suitedDefault: 'Entrepreneurs, executives, financial advisors, real estate professionals and premium service providers.',
      highlightDefaults: [
        'Luxury aesthetic that stands out instantly',
        'Strong for high-value client conversations',
        'Conveys success and attention to detail',
      ],
    },
  },
  {
    ids: ['1758954190786-royal-ocean-v1__3_'],
    nameIncludes: ['royal ocean'],
    profile: {
      overviewKey: 'dcl_tpl_royal_ocean_overview',
      suitedKey: 'dcl_tpl_royal_ocean_suited',
      highlightKeys: ['dcl_tpl_royal_ocean_h1', 'dcl_tpl_royal_ocean_h2', 'dcl_tpl_royal_ocean_h3'],
      overviewDefault:
        'Deep ocean blues and a commanding layout create a bold professional presence. Royal Ocean suits people who want authority, clarity and a memorable visual identity.',
      suitedDefault: 'Business owners, corporate leaders, consultants and teams presenting a confident, established brand.',
      highlightDefaults: [
        'Bold colour palette with strong contrast',
        'Memorable for events and first meetings',
        'Professional with distinctive character',
      ],
    },
  },
  {
    ids: ['t_1786708162552_kgs3', 't_1760711848336'],
    componentKeys: ['classic-modular-oclassic-11055cd5-82f893b6'],
    nameIncludes: ['orange classic'],
    profile: {
      overviewKey: 'dcl_tpl_orange_classic_overview',
      suitedKey: 'dcl_tpl_orange_classic_suited',
      highlightKeys: ['dcl_tpl_orange_classic_h1', 'dcl_tpl_orange_classic_h2', 'dcl_tpl_orange_classic_h3'],
      overviewDefault:
        'Warm orange accents and a creative layout help your personality shine. Orange Classic is built for performers and creators who want energy, warmth and a card that feels alive.',
      suitedDefault: 'Musicians, performers, entertainers, creators and artists who want a vibrant, expressive digital identity.',
      highlightDefaults: [
        'Warm, creative tone that feels personal',
        'Built for social, streaming and fan engagement',
        'Stands out from standard business cards',
      ],
    },
  },
  {
    ids: ['t_1786708162473_v6lx', 't_1760690814973'],
    componentKeys: ['classic-modular-8ef1ef16'],
    nameIncludes: ['blue artist'],
    profile: {
      overviewKey: 'dcl_tpl_blue_artist_overview',
      suitedKey: 'dcl_tpl_blue_artist_suited',
      highlightKeys: ['dcl_tpl_blue_artist_h1', 'dcl_tpl_blue_artist_h2', 'dcl_tpl_blue_artist_h3'],
      overviewDefault:
        'A deep blue header, centred avatar and action-first layout designed for creators on the move. Blue Artist brings your story, social profiles and streaming links together in one striking card.',
      suitedDefault: 'Artists, musicians, DJs, content creators and entertainment professionals building their audience.',
      highlightDefaults: [
        'Creator-first layout with social and streaming blocks',
        'Strong mobile sharing for gigs and collaborations',
        'Professional enough for bookings, creative enough for fans',
      ],
    },
  },
  {
    ids: ['t_1786708162234_z6kq'],
    componentKeys: ['classic-modular-e8a978c9'],
    nameIncludes: ['aurora gradient'],
    profile: {
      overviewKey: 'dcl_tpl_aurora_gradient_overview',
      suitedKey: 'dcl_tpl_aurora_gradient_suited',
      highlightKeys: ['dcl_tpl_aurora_gradient_h1', 'dcl_tpl_aurora_gradient_h2', 'dcl_tpl_aurora_gradient_h3'],
      overviewDefault:
        'Vibrant purple gradients and a stage-ready hero image create an immersive artist card. Aurora Gradient is designed for performers who want colour, energy and a memorable first impression.',
      suitedDefault: 'Singers, musicians, performers and creators who want a bold, expressive digital presence.',
      highlightDefaults: [
        'Gradient styling with a concert-ready visual feel',
        'Social and streaming blocks built for fan engagement',
        'Stands out instantly when shared on mobile',
      ],
    },
  },
  {
    ids: ['t_1786708162562_ad0v'],
    componentKeys: ['classic-modular-dark-9cc5d1cc-9b6c7b98'],
    nameIncludes: ['dark artist'],
    profile: {
      overviewKey: 'dcl_tpl_dark_artist_overview',
      suitedKey: 'dcl_tpl_dark_artist_suited',
      highlightKeys: ['dcl_tpl_dark_artist_h1', 'dcl_tpl_dark_artist_h2', 'dcl_tpl_dark_artist_h3'],
      overviewDefault:
        'A sleek dark header and clean grid layout give your card a refined, modern edge. Dark Artist balances professionalism with creative personality — ideal for artists who want sophistication without losing character.',
      suitedDefault: 'Artists, producers, DJs and creators who prefer a polished, contemporary aesthetic.',
      highlightDefaults: [
        'Dark, modern styling with strong visual contrast',
        'Organised contact, social and streaming sections',
        'Professional enough for bookings, distinctive enough for fans',
      ],
    },
  },
];

const CATEGORY_FALLBACK: Record<'business' | 'artist', TemplateGalleryProfile> = {
  business: {
    overviewKey: 'dcl_tpl_fallback_business_overview',
    suitedKey: 'dcl_tpl_fallback_business_suited',
    highlightKeys: ['dcl_tpl_fallback_business_h1', 'dcl_tpl_fallback_business_h2', 'dcl_tpl_fallback_business_h3'],
    overviewDefault:
      'A professional TCards template designed to present your identity, contacts and links in one polished digital card you can share anywhere.',
    suitedDefault: 'Professionals, entrepreneurs, consultants and businesses building a credible digital presence.',
    highlightDefaults: [
      'Professional layout ready to personalise',
      'Share via link, QR code or messaging apps',
      'Update anytime without changing your link',
    ],
  },
  artist: {
    overviewKey: 'dcl_tpl_fallback_artist_overview',
    suitedKey: 'dcl_tpl_fallback_artist_suited',
    highlightKeys: ['dcl_tpl_fallback_artist_h1', 'dcl_tpl_fallback_artist_h2', 'dcl_tpl_fallback_artist_h3'],
    overviewDefault:
      'A creative TCards template that brings your identity, social presence and important links together in a design built for creators.',
    suitedDefault: 'Musicians, artists, performers and creators who want a distinctive digital identity.',
    highlightDefaults: [
      'Creative layout with room for your story',
      'Connect social and streaming profiles easily',
      'Share your card at gigs, online and in DMs',
    ],
  },
};

function normalizeCategory(template: TemplateMeta): 'business' | 'artist' {
  const c = String(template.category || '').toLowerCase();
  return c.includes('artist') ? 'artist' : 'business';
}

function matchesRule(template: TemplateMeta, rule: MatchRule): boolean {
  const id = String(template.id || '');
  const key = String(template.componentKey || '').toLowerCase();
  const name = String(template.name || '').toLowerCase();
  if (rule.ids?.some((x) => x === id)) return true;
  if (rule.componentKeys?.some((x) => key === x.toLowerCase() || key.startsWith(x.toLowerCase()))) return true;
  if (rule.nameIncludes?.some((frag) => name.includes(frag.toLowerCase()))) return true;
  return false;
}

export function resolveTemplateGalleryProfile(template: TemplateMeta): TemplateGalleryProfile {
  for (const rule of PROFILES) {
    if (matchesRule(template, rule)) return rule.profile;
  }
  return CATEGORY_FALLBACK[normalizeCategory(template)];
}

export type ResolvedTemplateCopy = {
  overview: string;
  suitedFor: string;
  highlights: string[];
};

export function resolveTemplateGalleryCopy(
  template: TemplateMeta,
  t: (key: string, options?: Record<string, unknown>) => string
): ResolvedTemplateCopy {
  const profile = resolveTemplateGalleryProfile(template);
  const serverDesc = String(template.description || '').trim();
  const overview =
    t(profile.overviewKey, { defaultValue: profile.overviewDefault }) ||
    (serverDesc.length > 20 ? serverDesc : profile.overviewDefault);
  const suitedFor = t(profile.suitedKey, { defaultValue: profile.suitedDefault });
  const highlights = profile.highlightKeys.map((key, i) =>
    t(key, { defaultValue: profile.highlightDefaults[i] })
  );
  return { overview, suitedFor, highlights };
}
